import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStoreSetup } from '../../hooks/useStoreSetup';
import Loading from '../../components/common/Loading';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

export default function StoreImages() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    handleUploadStoreImage, 
    fetchSellerProfile, 
    uploadLoading 
  } = useStoreSetup();

  // Image states
  const [storeLogo, setStoreLogo] = useState(null);
  const [storeProfileImage, setStoreProfileImage] = useState(null);
  const [storeCoverImage, setStoreCoverImage] = useState(null);
  
  // Individual loading states
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Modal states
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [imageToRemove, setImageToRemove] = useState(null);

  /* ------------------------------------------------------------------ */
  /*  LOAD EXISTING IMAGES                                             */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    loadSellerProfile();
  }, []);

  const loadSellerProfile = async () => {
    try {
      const profile = await fetchSellerProfile(user.user_id);
      if (profile) {
        setStoreLogo(profile?.store_logo_url || null);
        setStoreProfileImage(profile?.store_profile_picture_url || null);
        setStoreCoverImage(profile?.store_cover_picture_url || null);
      }
    } catch (err) {
      // console.error('Failed to load seller profile:', err);
      // You can add toast notification here
    } finally {
      setInitialLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  IMAGE UPLOAD HANDLERS                                            */
  /* ------------------------------------------------------------------ */
  const pickImage = async (imageType) => {
    try {
      // Create file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      
      // Set aspect ratio constraints
      if (imageType === 'cover') {
        // Optional: You can't enforce aspect ratio in file picker, but you can warn the user
      }
      
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
          }

          // Validate file size (5MB max)
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (file.size > maxSize) {
            alert('File size should be less than 5MB');
            return;
          }

          // Create preview URL
          const previewUrl = URL.createObjectURL(file);
          
          // Upload image
          await uploadImage(file, imageType, previewUrl);
        }
      };
      
      input.click();
    } catch (error) {
      // console.error('Image picker error:', error);
      // Add toast notification here
    }
  };

  const uploadImage = async (file, imageType, previewUrl) => {
    try {
      // Set individual loading state
      const loadingStates = {
        logo: setUploadingLogo,
        profile: setUploadingProfile,
        cover: setUploadingCover
      };
      loadingStates[imageType](true);

      // Update local preview immediately for better UX
      const stateSetters = {
        logo: setStoreLogo,
        profile: setStoreProfileImage,
        cover: setStoreCoverImage
      };
      stateSetters[imageType](previewUrl);

      // Upload image using your API service
      // Note: handleUploadStoreImage expects (userId, imageUri, imageType)
      // For web, we'll pass the File object as the second parameter
      const result = await handleUploadStoreImage(user.user_id, file, imageType);
      
      // Update with the actual URL from API response
      if (result?.imageUrl || result?.url) {
        const imageUrl = result.imageUrl || result.url;
        stateSetters[imageType](imageUrl);
      }

      // Show success message (you can replace with toast)
      // console.log(`${imageType} image uploaded successfully!`);
      
      // If you have a toast system, use it here
      // toast.success(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} image updated successfully!`);
    } catch (error) {
      // console.error('Upload error:', error);
      // Revert to previous image on error
      const stateSetters = {
        logo: setStoreLogo,
        profile: setStoreProfileImage,
        cover: setStoreCoverImage
      };
      // You might want to fetch the current image from server again
      // For now, we'll just clear the preview
      stateSetters[imageType](null);
      
      // Show error message
      alert(error.message || 'Failed to upload image. Please try again.');
      // toast.error(error.message || 'Failed to upload image. Please try again.');
    } finally {
      const loadingStates = {
        logo: setUploadingLogo,
        profile: setUploadingProfile,
        cover: setUploadingCover
      };
      loadingStates[imageType](false);
    }
  };

  const confirmRemoveImage = (imageType) => {
    setImageToRemove(imageType);
    setShowRemoveModal(true);
  };

  const removeImage = async () => {
    try {
      const imageType = imageToRemove;
      
      // For web, you might need to call a remove API endpoint
      // If your API supports it, you can create a removeStoreImage function
      // For now, we'll just clear it locally
      
      const stateSetters = {
        logo: setStoreLogo,
        profile: setStoreProfileImage,
        cover: setStoreCoverImage
      };
      stateSetters[imageType](null);
      
      // Optionally call an API to remove the image from server
      // await removeStoreImage(user.user_id, imageType);
      
      // Show success message
      // console.log(`${imageType} image removed successfully!`);
      // toast.success(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} image removed!`);
    } catch (error) {
      // console.error('Remove error:', error);
      // toast.error('Failed to remove image. Please try again.');
    } finally {
      setShowRemoveModal(false);
      setImageToRemove(null);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  /* ------------------------------------------------------------------ */
  /*  IMAGE UPLOAD COMPONENTS                                          */
  /* ------------------------------------------------------------------ */
  const ImageUploadSection = ({ 
    title, 
    imageUri, 
    imageType, 
    description, 
    loading, 
    onPickImage, 
    onRemoveImage 
  }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm mb-4">{description}</p>
      
      <div 
        className={`
          relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden
          ${imageType === 'cover' ? 'h-40' : 'h-48'}
          ${!imageUri ? 'cursor-pointer hover:border-orange-400 transition' : ''}
        `}
        onClick={!imageUri && !loading ? () => onPickImage(imageType) : undefined}
      >
        {loading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <p className="mt-3 text-orange-600 font-medium">Uploading...</p>
          </div>
        ) : imageUri ? (
          <div className="relative h-full">
            <img 
              src={imageUri} 
              alt={title}
              className={`w-full h-full object-cover ${imageType === 'cover' ? '' : 'object-center'}`}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 group">
              <div className="absolute top-3 right-3 flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveImage(imageType);
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition"
                  title="Remove image"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPickImage(imageType);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg shadow-lg transition flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm font-medium">Replace</span>
                </button>
              </div>
              <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Saved
              </div>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 cursor-pointer hover:bg-gray-100 transition">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium mb-1">
              Click to upload {title.toLowerCase()}
            </p>
            <p className="text-gray-500 text-sm text-center px-4">
              {imageType === 'logo' ? 'Square image recommended (1:1)' :
               imageType === 'profile' ? 'Square image recommended (1:1)' :
               'Wide image recommended (16:9)'}
            </p>
            <p className="text-gray-400 text-xs mt-2">JPG, PNG up to 5MB</p>
          </div>
        )}
      </div>
      
      {imageUri && (
        <div className="mt-3 flex items-center gap-2 text-green-600">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium">
            {title} is saved and live on your store
          </span>
        </div>
      )}
    </div>
  );

  /* ------------------------------------------------------------------ */
  /*  RENDER LOGIC                                                     */
  /* ------------------------------------------------------------------ */
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading size="large" />
          <p className="mt-4 text-gray-600">Loading store images...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <Header />
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Store Images</h1>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Info Banner */}
          <div className="bg-blue-50 border-l-4 border-orange-500 rounded-r-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-gray-700 text-sm">
                Images are saved automatically when you select them. Your store updates in real-time.
              </p>
            </div>
          </div>

          {/* Store Logo */}
          <ImageUploadSection
            title="Store Logo"
            imageUri={storeLogo}
            imageType="logo"
            description="Your store logo appears on your store page and listings. Use a square image (1:1 ratio) for best results."
            loading={uploadingLogo}
            onPickImage={pickImage}
            onRemoveImage={confirmRemoveImage}
          />

          {/* Store Cover Image */}
          <ImageUploadSection
            title="Store Cover Image"
            imageUri={storeCoverImage}
            imageType="cover"
            description="The cover image appears at the top of your store page. Use a wide, high-quality image (16:9 ratio recommended)."
            loading={uploadingCover}
            onPickImage={pickImage}
            onRemoveImage={confirmRemoveImage}
          />

          {/* Done Button */}
          <div className="mt-8">
            <button
              onClick={handleBack}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition shadow-sm hover:shadow"
            >
              Done
            </button>
          </div>

          {/* Note */}
          <div className="mt-6 flex items-center justify-center gap-2 text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm italic">
              Changes appear immediately on your store page
            </p>
          </div>
        </div>
        <Footer />
      </div>

      {/* Remove Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false);
          setImageToRemove(null);
        }}
        onConfirm={removeImage}
        title="Remove Image"
        message={`Are you sure you want to remove this ${imageToRemove} image?`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmColor="red"
      />
    </>
  );
}